import { cn } from '../../lib/utils';
import { CountBadge } from './CountBadge';

interface ListItem {
  readonly id: string;
  readonly value: string;
  readonly label: string;
  /**
   * Optional result-count badge rendered right-aligned next to the
   * label. Used by filter dropdowns (Providers OG + Metro on PR 1;
   * Detail / Map / Rankings in later PRs) to surface "how many results
   * would this option produce?" inline. Pass `undefined` to render no
   * badge (loading state, default "All ..." row, or callers that don't
   * have counts).
   */
  readonly count?: number;
}

interface ComboboxListboxProps {
  readonly id: string;
  readonly ariaLabel: string;
  readonly items: ReadonlyArray<ListItem>;
  readonly highlight: number;
  readonly selectedValue: string;
  readonly optionIdStem: string;
  readonly onHover: (idx: number) => void;
  readonly onCommit: (item: ListItem) => void;
  readonly emptyLabel?: string;
}

/**
 * Generic listbox panel rendered below a combobox input. Extracted
 * from `OccupationGroupCombobox` so the parent file stays under the
 * 250-line file budget (CLAUDE.md convention).
 *
 * Renders each item as `role="option"`. Commits on `mousedown` (not
 * click) so the pick fires before the input's blur kills `isOpen` --
 * if we waited for click, the listbox would unmount first.
 */
export function ComboboxListbox({
  id,
  ariaLabel,
  items,
  highlight,
  selectedValue,
  optionIdStem,
  onHover,
  onCommit,
  emptyLabel = 'No matches',
}: ComboboxListboxProps) {
  return (
    <ul
      id={id}
      role="listbox"
      aria-label={ariaLabel}
      className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[360px] overflow-y-auto rounded-md border border-border bg-white py-1 shadow-panel"
    >
      {items.length === 0 ? (
        <li className="px-3 py-2 text-sm italic text-text-4">{emptyLabel}</li>
      ) : (
        items.map((it, i) => {
          const isHi = i === highlight;
          const isSelected = selectedValue === it.value && it.value !== '';
          return (
            <li
              key={it.id}
              id={`${optionIdStem}-${it.id}`}
              role="option"
              aria-selected={isSelected || isHi}
              onMouseEnter={() => onHover(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                onCommit(it);
              }}
              className={cn(
                'flex cursor-pointer items-start gap-2 px-3 py-1.5 text-sm leading-[1.35] transition',
                isHi ? 'bg-ice text-navy' : 'text-text-1 hover:bg-ice hover:text-navy',
                isSelected && 'font-semibold text-navy',
              )}
            >
              {/* 2026-07-02 (Georgetown feedback): long OG names
                  ("Installation, maintenance, and equipment repair")
                  were being truncated in the middle. Swapped from
                  `truncate` to `break-words` so labels wrap to a
                  second line instead. `items-start` up on the <li>
                  keeps the badge aligned with the first line of a
                  wrapped label. */}
              <span className="min-w-0 flex-1 break-words">{it.label}</span>
              {/* Right-aligned count badge -- only renders when the
                  caller supplied a count for this option. Zero is a
                  valid render (muted gray) so empty buckets stay
                  visible; loading / no-counts state passes
                  `undefined` and the badge is omitted. */}
              {typeof it.count === 'number' && (
                <CountBadge value={it.count} className="shrink-0" />
              )}
            </li>
          );
        })
      )}
    </ul>
  );
}

export type { ListItem as ComboboxListItem };
